class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(
        ':scope > template[shadowrootmode="open"]'
      );
      if (!(template instanceof HTMLTemplateElement)) return;
      this.attachShadow({ mode: "open" }).append(template.content.cloneNode(true));
    }
  }
}

class ResizeNotifier extends ResizeObserver {
  #called = false;

  constructor(callback) {
    super((entries) => {
      if (this.#called) return;
      this.#called = true;
      callback(entries, this);
    });
  }

  disconnect() {
    this.#called = false;
    super.disconnect();
  }
}

class OverflowMinimumEvent extends Event {
  constructor(minimumReached) {
    super("overflowMinimum", { bubbles: true });
    this.detail = { minimumReached };
  }
}

class ReflowEvent extends Event {
  constructor(lastVisibleElement) {
    super("reflow", { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ["disabled", "minimum-items"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "disabled") {
      newValue === "true" ? this.#disable() : this.#enable();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const { shadowRoot } = this;
    if (!shadowRoot) throw new Error("Missing shadow root");

    const defaultSlot = shadowRoot.querySelector("slot:not([name])");
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflowPart = shadowRoot.querySelector('[part="overflow"]');
    const listPart = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(
        defaultSlot instanceof HTMLSlotElement &&
        overflowSlot instanceof HTMLSlotElement &&
        moreSlot instanceof HTMLSlotElement &&
        overflowPart instanceof HTMLElement &&
        listPart instanceof HTMLUListElement &&
        placeholder instanceof HTMLLIElement
      )
    )
      throw new Error("Invalid element types in <OverflowList />");

    this.#elements = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow: overflowPart,
      list: listPart,
      placeholder
    };

    this.addEventListener("reflow", (e) => {
      this.#enable(e.detail.lastVisibleElement);
    });

    if (this.hasAttribute("defer")) {
      const run = () => {
        this.removeAttribute("defer");
        this.#enable();
      };
      (typeof requestIdleCallback === "function" ? requestIdleCallback : setTimeout)(() =>
        this.schedule(run)
      );
    } else {
      this.#enable();
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === "function"
      ? Theme.utilities.scheduler.schedule
      : requestAnimationFrame;
  }

  #processing = false;

  get minimumItems() {
    const val = this.getAttribute("minimum-items");
    return val ? parseInt(val, 10) : null;
  }

  get overflowSlot() {
    return this.#elements.overflowSlot;
  }

  get defaultSlot() {
    return this.#elements.defaultSlot;
  }

  #size = { width: null, height: null };

  #onResize = (entries) => {
    if (this.#processing) return;

    let width = null,
      height = null,
      hasEntry = false;

    for (const [, entry] of entries.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      hasEntry = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (hasEntry) {
      if (!width || !height || (width === this.#size.width && height === this.#size.height))
        return;
      this.#size = { width, height };
    }

    this.#processing = true;
    this.schedule(() => {
      this.#enable();
      this.#processing = false;
    });
  };

  #resetSlots() {
    const { defaultSlot, overflowSlot } = this.#elements;
    for (const el of overflowSlot.assignedElements()) el.slot = defaultSlot.name;
  }

  #disable() {
    const { list } = this.#elements;
    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#resetSlots();
    list.style.removeProperty("height");
    this.style.setProperty("--overflow-count", "0");
  }

  #checkMinimum(elements) {
    if (this.minimumItems !== null) {
      const reached = elements.length < this.minimumItems;
      if (reached) this.setAttribute("minimum-reached", "");
      else this.removeAttribute("minimum-reached");
      this.dispatchEvent(new OverflowMinimumEvent(reached));
    }
  }

  showAll() {
    const { placeholder } = this.#elements;
    placeholder.style.setProperty("width", "0");
    placeholder.style.setProperty("display", "none");
    this.setAttribute("disabled", "true");
  }

  #enable(lastVisibleElement) {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#elements;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};
    if (height) list.style.height = `${height}px`;

    list.style.setProperty("overflow", "hidden");
    this.#resetSlots();

    const allItems = defaultSlot.assignedElements();
    moreSlot.hidden = true;
    placeholder.hidden = true;

    const listRect = list.getBoundingClientRect();
    this.#size = { width: Math.round(listRect.width), height: Math.round(listRect.height) };

    const getVisible = () =>
      allItems.filter((el) => el.getBoundingClientRect().top <= listRect.top);

    let visibleItems = getVisible();

    if (visibleItems.length !== allItems.length || lastVisibleElement) {
      moreSlot.style.setProperty("order", "-1");
      moreSlot.hidden = false;
      moreSlot.style.setProperty("height", `${height}px`);

      if (lastVisibleElement) lastVisibleElement.style.setProperty("order", "-1");
      visibleItems = getVisible();

      moreSlot.style.removeProperty("order");
      if (lastVisibleElement) lastVisibleElement.style.removeProperty("order");

      if (lastVisibleElement) {
        const index = visibleItems.indexOf(lastVisibleElement);
        if (index !== -1) {
          visibleItems.splice(index, 1);
          visibleItems.push(lastVisibleElement);
        }
      }

      moreSlot.style.setProperty("height", "auto");
    }

    const overflowItems = allItems.filter((el) => !visibleItems.includes(el));
    const [firstOverflow] = overflowItems;
    const hasOverflow = overflowItems.length > 0;
    const overflowWidth = firstOverflow ? firstOverflow.clientWidth : 0;

    for (const el of allItems) el.slot = overflowItems.includes(el) ? overflowSlot.name : defaultSlot.name;

    moreSlot.hidden = !hasOverflow;
    if (hasOverflow) {
      placeholder.style.width = `${overflowWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty("counter-reset", `overflow-count ${overflowItems.length}`);
    this.style.setProperty("--overflow-count", `${overflowItems.length}`);
    list.style.removeProperty("overflow");

    if (hasOverflow) this.#checkMinimum(visibleItems);

    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  }

  #elements;
  #resizeObserver = new ResizeNotifier(this.#onResize);
  #mutationObserver = new MutationObserver(this.#onResize);
}

function calculateHeaderGroupHeight(header = document.querySelector("#header-component"), group = document.querySelector("#header-group")) {
  if (!group) return 0;
  let height = 0;
  const children = group.children;
  for (let i = 0; i < children.length; i++) {
    const el = children[i];
    if (el !== header && el instanceof HTMLElement) height += el.offsetHeight;
  }
  return height;
}

customElements.get("overflow-list") || customElements.define("overflow-list", OverflowList);

(() => {
  const header = document.querySelector("header-component");
  if (!(header instanceof HTMLElement)) return;
  const headerHeight = header.offsetHeight;
  const groupHeight = calculateHeaderGroupHeight(header);
  document.body.style.setProperty("--header-height", `${headerHeight}px`);
  document.body.style.setProperty("--header-group-height", `${groupHeight}px`);
})();

